import { ClampToEdgeWrapping, DataTexture, FloatType, NearestFilter, RGBAFormat } from 'three/webgpu';
import { evalCurve } from './math/curve.js';
import { evalGradient } from './math/gradient.js';
/**
 * Curve atlas: one small RGBA float texture per layer, rows = curves/gradients.
 * Scalar curves are broadcast across RGBA; gradients use all four channels.
 *
 * Format decision (recorded in NOTES.md): RGBA32F + NearestFilter + manual
 * linear interpolation in the shader. Float linear filtering is an optional
 * WebGL2 extension (OES_texture_float_linear) — manual lerp of two nearest
 * texels is backend-proof and bit-identical to this CPU reference, which is
 * what makes the CPU↔GPU tolerance tests meaningful.
 *
 * The row count is fixed at construction so live re-bakes never reallocate the
 * texture — the shader keeps sampling the same texture object and picks up new
 * data on `commit()` without a material rebuild.
 */
export const ATLAS_WIDTH = 256;
export const ATLAS_ROWS = 8;
const scratchRgba = [0, 0, 0, 0];
export class CurveAtlas {
    width = ATLAS_WIDTH;
    rows;
    data;
    texture;
    constructor(rows = ATLAS_ROWS) {
        this.rows = rows;
        this.data = new Float32Array(this.width * rows * 4);
        this.texture = new DataTexture(this.data, this.width, rows, RGBAFormat, FloatType);
        this.texture.minFilter = NearestFilter;
        this.texture.magFilter = NearestFilter;
        this.texture.wrapS = ClampToEdgeWrapping;
        this.texture.wrapT = ClampToEdgeWrapping;
        this.texture.generateMipmaps = false;
        this.texture.needsUpdate = true;
    }
    setCurve(row, curve) {
        const { data, width } = this;
        for (let i = 0; i < width; i++) {
            const v = evalCurve(curve, i / (width - 1));
            const base = (row * width + i) * 4;
            data[base] = v;
            data[base + 1] = v;
            data[base + 2] = v;
            data[base + 3] = v;
        }
    }
    setGradient(row, gradient) {
        const { data, width } = this;
        for (let i = 0; i < width; i++) {
            const c = evalGradient(gradient, i / (width - 1), scratchRgba);
            const base = (row * width + i) * 4;
            data[base] = c[0];
            data[base + 1] = c[1];
            data[base + 2] = c[2];
            data[base + 3] = c[3];
        }
    }
    /** Flag the texture for re-upload after one or more set* calls. */
    commit() {
        this.texture.needsUpdate = true;
    }
    /** CPU emulation of the shader's manual-lerp sampling — the test oracle. */
    sample(row, t, out = [0, 0, 0, 0]) {
        const { data, width } = this;
        const x = Math.min(Math.max(t, 0), 1) * (width - 1);
        const x0 = Math.floor(x);
        const x1 = Math.min(x0 + 1, width - 1);
        const f = x - x0;
        const a = (row * width + x0) * 4;
        const b = (row * width + x1) * 4;
        for (let c = 0; c < 4; c++)
            out[c] = data[a + c] + (data[b + c] - data[a + c]) * f;
        return out;
    }
    sampleScalar(row, t) {
        return this.sample(row, t, scratchRgba)[0];
    }
    dispose() {
        this.texture.dispose();
    }
}
